import React, { useState, useEffect, useMemo } from "react";
import { Card, Space, Table, Typography, Select, Row, Col } from "antd";
import { ColumnsType } from 'antd/es/table';
import { useGetAllUsersQuery } from "../api/api";
import Spinner from "../components/Spinner";
import { Error } from "../components/Error.component";
import SearchBox from "../components/SearchBox";
import { Link } from "react-router-dom";

const { Title } = Typography;
const { Option } = Select;

function filterData(data, searchQuery, typeFilter, businessUnitFilter, departmentFilter) {
  if (!data) return []; // Add this check

  if (!searchQuery && !typeFilter && !businessUnitFilter && !departmentFilter) {
    return data.filter((item) => parseFloat(item.productsPrice) > 0);
  }

  const query = searchQuery.toLowerCase();

  return data.filter((item) => {
    const matchesSearchQuery = Object.values(item).some((value) =>
      String(value).toLowerCase().includes(query)
    );
    const matchesTypeFilter = typeFilter
      ? (typeFilter === "C" ? item.irdetoemployeeid_0 === "C" : item.irdetoemployeeid_0 !== "C")
      : true;
    const matchesBusinessUnitFilter = businessUnitFilter
      ? item.businessunit_0 === businessUnitFilter
      : true;
    const matchesDepartmentFilter = departmentFilter
      ? item.departmentcode_0 === departmentFilter
      : true;
    const hasNonZeroCost = parseFloat(item.productsPrice) > 0;

    return matchesSearchQuery && matchesTypeFilter && matchesBusinessUnitFilter && matchesDepartmentFilter && hasNonZeroCost;
  });
}



export const Users = () => {
  const { data, error, isLoading } = useGetAllUsersQuery("");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [businessUnitFilter, setBusinessUnitFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [businessUnits, setBusinessUnits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [contractorsCount, setContractorsCount] = useState(0);
  const [employeesCount, setEmployeesCount] = useState(0);

  const errorMessage = {
    status: "Unknown",
    message: "An error occurred while fetching users.",
  };



  useEffect(() => {
    if (data) {
      const uniqueBusinessUnits = Array.from(
        new Set(data.data.map((item) => item.businessunit_0))
      );
      setBusinessUnits(uniqueBusinessUnits);

      const uniqueDepartments = Array.from(
        new Set(data.data.map((item) => item.departmentcode_0))
      );
      setDepartments(uniqueDepartments);

      const contractors = data.data.filter(user => user.irdetoemployeeid_0 === "C").length;
      const employees = data.data.filter(user => user.irdetoemployeeid_0 !== "C").length;
      setContractorsCount(contractors);
      setEmployeesCount(employees);
    }
  }, [data]);

  let users = null;
  if (data) users = data.data;

  const filteredUsers = useMemo(() => {
    return filterData(users, searchQuery, typeFilter, businessUnitFilter, departmentFilter);
  }, [users, searchQuery, typeFilter, businessUnitFilter, departmentFilter]);
  

  const totalAnnualLicenseCost = filteredUsers ? filteredUsers.reduce((total, user) => total + parseFloat(user.productsPrice) * 12, 0) : 0;
  const totalUsers = filteredUsers ? filteredUsers.length : 0;
  const averageLicenseCost = totalUsers > 0 ? totalAnnualLicenseCost / totalUsers : 0;

  const columns: ColumnsType<any> = [
    {
      title: "Name",
      dataIndex: "employeename_0",
      key: "name",
    },
    {
      title: "Employee Type",
      dataIndex: "irdetoemployeeid_0",
      key: "irdetoemployeeid_0",
      render: (text) => (text === "C" ? "Contractor" : "Employee"),
    },
    {
      title: "Business Unit",
      dataIndex: "businessunit_0",
      key: "product",
      sorter: (a, b) => a.product - b.product,
    },
    {
      title: "Department",
      dataIndex: "departmentcode_0",
      key: "departmentcode_0",
      sorter: (a, b) => a.departmentcode_0 - b.departmentcode_0,
    },
    {
      title: "Assigned licenses",
      dataIndex: "productsCount",
      key: "productsCount",
      sorter: (a, b) => a.productsCount - b.productsCount,
      render: (value) => (
        <div style={{ textAlign: 'right' }}>
          {value.toLocaleString()}
        </div>
      ),
      className: "table-cell-padding"
    },
    {
      title: "License cost (Year)",
      dataIndex: "productsPrice",
      key: "productsPrice",
      render: (value) => (
        <div style={{ textAlign: 'right' }}>
          {`$${Math.round(parseFloat(value) * 12).toLocaleString()}`}
        </div>
      ),
      sorter: (a, b) => a.productsPrice - b.productsPrice,
      className: "table-cell-padding"
    },
    
    
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (text, row) => {
        return <Link to={`/user/${row.username_0}`}>Details</Link>;
      },
    },
  ];

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  return (
    <div className="montserrat-font">
      {isLoading && <Spinner />}
      {error && <Error error={errorMessage} />}
      {data && !error && (
        <Space direction="vertical" size="middle" style={{ display: "flex" }}>
          <Row gutter={16}>
            <Col span={12}>
            <Card className="muse-card equal-height-card">
                <h3>Total assigned license cost: <strong>${Math.round(totalAnnualLicenseCost).toLocaleString()} </strong> / Year</h3>
                <h3>Average license cost per user: <strong>${averageLicenseCost.toFixed(2)} </strong> / Year</h3>
                <h3><strong></strong></h3>
              </Card>
            </Col>
            <Col span={12}>
            <Card className="muse-card equal-height-card">
                <h3>Total Users: <strong>{totalUsers}</strong></h3>
                <h3>Total Employees: <strong>{employeesCount}</strong></h3>
                <h3>Total Contractors: <strong>{contractorsCount}</strong></h3>
              </Card>
            </Col>
            
          </Row>
          <Card className="muse-card">
            <Space direction="horizontal" size="middle" style={{ marginBottom: 12 }}>
              <SearchBox onSearch={setSearchQuery} />
              <Select
                placeholder="Filter by Employee Type"
                style={{ width: 200 }}
                onChange={setTypeFilter}
                allowClear
              >
                <Option value="">All</Option>
                <Option value="C">Contractor</Option>
                <Option value="E">Employee</Option>
              </Select>
              <Select
                placeholder="Filter by Business Unit"
                style={{ width: 200 }}
                onChange={setBusinessUnitFilter}
                allowClear
              >
                <Option value="">All</Option>
                {businessUnits.map((unit) => (
                  <Option key={unit} value={unit}>
                    {unit}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="Filter by Department"
                style={{ width: 200 }}
                onChange={setDepartmentFilter}
                allowClear
              >
                <Option value="">All</Option>
                {departments.map((dept) => (
                  <Option key={dept} value={dept}>
                    {dept}
                  </Option>
                ))}
              </Select>
            </Space>
            <Table
              dataSource={filteredUsers}
              columns={columns}
              rowKey="username_0"
              pagination={pagination}
              onChange={handleTableChange}
              className="montserrat-font"
              rowClassName={() => "table-row-spacing"}
            />
          </Card>
        </Space>
      )}
    </div>
  );
};
